import * as THREE from 'three';
import { SUBTRACTION, Brush, Evaluator } from 'three-bvh-csg';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader';
import tahomaFont from '../assets/Tahoma_Bold.typeface.json';
import { prepareGeometry } from 'Utils/ViewerUtils';
import { assignMatchingFields } from 'Utils/ModelUtils';

export class Engraving {
    engraveText: boolean | null = true;
    textVertical: boolean | null = true;
    textString: string = '';
    textDepth: number | null = 0.7;
    textHeight: number | null = 8;
    textRepeat: number | null = 1;
    textMesh: Brush[] | null = null;
    textPosition: THREE.Vector3 = new THREE.Vector3(-65, 0, 2);

    engraveUID: boolean | null = true;
    UIDVertical: boolean | null = true;
    UIDString: string = 'UID';
    UIDDepth: number | null = 0.7;
    UIDHeight: number | null = 5;
    UIDRepeat: number | null = 1;
    UIDMesh: Brush[] | null = null;
    UIDPosition: THREE.Vector3 = new THREE.Vector3(-53, 0, -15);

    engraveCE: boolean | null = false;
    CEVertical: boolean | null = true;
    CEDepth: number | null = 0.7;
    CEHeight: number | null = 5;
    CEMesh: Brush[] | null = null;
    CEPosition: THREE.Vector3 = new THREE.Vector3(-53, 0, -20);

    engraveLogo: boolean = false;
    invertLogo: boolean = true;
    logoAddress: string = 'Logo';
    logoDepth: number = 0.7;
    logoResolution: number = 5;
    logoWidth: number = 1;
    logoMesh: Brush[] | null = null;
    logoPosition: THREE.Vector3 = new THREE.Vector3(-45,0, 0);

    engravedGeometry: THREE.BufferGeometry | null = null;

    csgEvaluator = new Evaluator();

    constructor(init?: Partial<Engraving>) {
        if (init) assignMatchingFields(this, init);
        this.updateText();
        this.updateUID();
        this.updateCE();
    }

    static cloneFrom(original: Engraving): Engraving {
        const copy = new Engraving();

        // ===== Text =====
        copy.engraveText = original.engraveText;
        copy.textVertical = original.textVertical;
        copy.textString = original.textString;
        copy.textDepth = original.textDepth;
        copy.textHeight = original.textHeight;
        copy.textRepeat = original.textRepeat;
        copy.textPosition = original.textPosition.clone();

        // ===== UID =====
        copy.engraveUID = original.engraveUID;
        copy.UIDVertical = original.UIDVertical;
        copy.UIDString = original.UIDString;
        copy.UIDDepth = original.UIDDepth;
        copy.UIDHeight = original.UIDHeight;
        copy.UIDRepeat = original.UIDRepeat;
        copy.UIDPosition = original.UIDPosition.clone();

        // ===== CE =====
        copy.engraveCE = original.engraveCE;
        copy.CEVertical = original.CEVertical;
        copy.CEDepth = original.CEDepth;
        copy.CEHeight = original.CEHeight;
        copy.CEPosition = original.CEPosition.clone();

        // ===== Logo =====
        copy.engraveLogo = original.engraveLogo;
        copy.invertLogo = original.invertLogo;
        copy.logoAddress = original.logoAddress;
        copy.logoDepth = original.logoDepth;
        copy.logoResolution = original.logoResolution;
        copy.logoWidth = original.logoWidth;
        copy.logoPosition = original.logoPosition.clone();

        // ===== Engraved Geometry =====
        copy.engravedGeometry = original.engravedGeometry
            ? original.engravedGeometry.clone()
            : null;

        // ===== Meshes =====
        copy.textMesh = null;
        copy.UIDMesh = null;
        copy.CEMesh = null;
        copy.logoMesh = null;

        copy.csgEvaluator = new Evaluator();

        // Rebuild derived meshes
        copy.updateText();
        copy.updateUID();
        copy.updateCE();

        return copy;
    }

    Engrave = (insole: THREE.BufferGeometry, foot: number) => {
        try {
            this.csgEvaluator.attributes = ['position', 'normal'];

            // Prepare STL geometry and create brush
            const stlGeomPrepared = prepareGeometry(insole);
            let insoleBrush = new Brush(stlGeomPrepared);
            insoleBrush.updateMatrix();
            insoleBrush.updateMatrixWorld();

            if (this.textMesh) 
                insoleBrush = this.csgEvaluator.evaluate(insoleBrush, this.textMesh[foot], SUBTRACTION);
            
            if (this.UIDMesh)
                insoleBrush = this.csgEvaluator.evaluate(insoleBrush, this.UIDMesh[foot], SUBTRACTION);

            if (this.CEMesh)
                insoleBrush = this.csgEvaluator.evaluate(insoleBrush, this.CEMesh[foot], SUBTRACTION);

            if (this.logoMesh)
                insoleBrush = this.csgEvaluator.evaluate(insoleBrush, this.logoMesh[foot], SUBTRACTION);

            return insoleBrush.geometry.clone();
        } catch (err) {
            console.error('Engraving failed: ' + (err instanceof Error ? err.message : err));
            return insole;
        }
    };

    CreateStringMesh = (text: string, position: THREE.Vector3, size: number, depth: number, rotation: number, type: string) => {
        const font = new FontLoader().parse(tahomaFont as any);
        const geometry = new TextGeometry(text, {
            font: font,
            size: size,
            depth: depth,
            curveSegments: 4,
            bevelEnabled: true,
            bevelThickness: 0.0,
            bevelSize: 0.0,
            bevelOffset: 0,
            bevelSegments: 1,
        } as any);
        geometry.computeVertexNormals();
        geometry.computeBoundingBox();
        geometry.center();
        geometry.rotateX(Math.PI / 2);
        geometry.rotateY(rotation * -Math.PI / 2);
        geometry.translate(0, depth / 2 - 0.3, 0);

        const material = new THREE.MeshStandardMaterial({ color: "#333333", side: THREE.DoubleSide });

        const textBrush = new Brush(geometry, material);
        textBrush.position.set(...position.toArray());
        textBrush.updateMatrix();
        textBrush.updateMatrixWorld();
        textBrush.name = 'engraving';
        textBrush.userData.position = position;
        textBrush.userData.type = type;

        return textBrush;
    }

    updateText(newText: string = '') {
        if (newText != '') this.textString = newText;

        let text = this.textString;
        if (this.textRepeat && this.textRepeat > 1)
            text = Array(this.textRepeat).fill(text).join('-');

        if (this.engraveText && this.textHeight && this.textDepth) {
            const rightmesh = this.CreateStringMesh(
                text,
                this.textPosition,
                this.textHeight,
                this.textDepth,
                this.textVertical ? 1 : 0,
                'Text'
            );
            const leftmesh = this.CreateStringMesh(
                text,
                new THREE.Vector3(this.textPosition.x * -1, this.textPosition.y, this.textPosition.z),
                this.textHeight,
                this.textDepth,
                this.textVertical ? 1 : 0,
                'Text'
            );
            this.textMesh = [rightmesh, leftmesh];
        }
        else {
            this.textMesh = null;
        }
    }

    updateUID(newUID: string = '') {
        if (newUID != '') this.UIDString = newUID;

        let text = this.UIDString;
        if (this.UIDRepeat && this.UIDRepeat > 1)
            text = Array(this.UIDRepeat).fill(text).join('-');

        if (this.engraveUID && this.UIDHeight && this.UIDDepth) {
            const rightmesh = this.CreateStringMesh(
                text,
                this.UIDPosition,
                this.UIDHeight,
                this.UIDDepth,
                this.UIDVertical ? 1 : 0,
                'UID'
            );
            const leftmesh = this.CreateStringMesh(
                text,
                new THREE.Vector3(this.UIDPosition.x * -1, this.UIDPosition.y, this.UIDPosition.z),
                this.UIDHeight,
                this.UIDDepth,
                this.UIDVertical ? 1 : 0,
                'UID'
            );
            this.UIDMesh = [rightmesh, leftmesh];
        }
        else {
            this.UIDMesh = null;
        }
    }

    updateCE() {
        if (this.engraveCE && this.CEHeight && this.CEDepth) {
            const rightmesh = this.CreateStringMesh(
                'CE',
                this.CEPosition,
                this.CEHeight,
                this.CEDepth,
                this.CEVertical ? 1 : 0,
                'CE'
            );
            const leftmesh = this.CreateStringMesh(
                'CE',
                new THREE.Vector3(this.CEPosition.x * -1, this.CEPosition.y, this.CEPosition.z),
                this.CEHeight,
                this.CEDepth,
                this.CEVertical ? 1 : 0,
                'CE'
            );
            this.CEMesh = [rightmesh, leftmesh];
        }
        else {
            this.CEMesh = null;
        }
    }


}

export class EngravingRequestDto {
    engraveText: boolean | null = null;
    textVertical: boolean | null = null;
    textDepth: number | null = null;
    textHeight: number | null = null;
    textRepeat: number | null = null;
    textX: number | null = null;
    textY: number | null = null;
    textZ: number | null = null;

    engraveUID: boolean | null = null;
    UIDVertical: boolean | null = null;
    UIDDepth: number | null = null;
    UIDHeight: number | null = null;
    UIDRepeat: number | null = null;
    UIDX:number | null = null;
    UIDY:number | null = null;
    UIDZ:number | null = null;

    engraveCE: boolean | null = null;
    CEVertical: boolean | null = null;
    CEDepth: number | null = null;
    CEHeight: number | null = null;
    CEX:number | null = null;
    CEY:number | null = null;
    CEZ:number | null = null;

    constructor(init?: Partial<EngravingRequestDto>) {
        if (init) assignMatchingFields(this, init);
    }

    asJSON() {
        return JSON.stringify(this);
    }

    toEngravingModel() {
        const engravingModel = new Engraving(this);
        if ((this.textX != null && this.textX != undefined) ||
            (this.textY != null && this.textY != undefined) ||
            (this.textZ != null && this.textZ != undefined))
            engravingModel.textPosition = new THREE.Vector3(this.textX ?? undefined, this.textY ?? undefined, this.textZ ?? undefined);
        if ((this.UIDX != null && this.UIDX != undefined) ||
            (this.UIDY != null && this.UIDY != undefined) ||
            (this.UIDZ != null && this.UIDZ != undefined))
            engravingModel.UIDPosition = new THREE.Vector3(this.UIDX ?? undefined, this.UIDY ?? undefined, this.UIDZ ?? undefined);
        if ((this.CEX != null && this.CEX != undefined) ||
            (this.CEY != null && this.CEY != undefined) ||
            (this.CEZ != null && this.CEZ != undefined))
            engravingModel.CEPosition = new THREE.Vector3(this.CEX ?? undefined, this.CEY ?? undefined, this.CEZ ?? undefined);

        engravingModel.updateText();
        engravingModel.updateUID();
        engravingModel.updateCE();

        return engravingModel;
    }
}

export class EngravingResponseDto {
    engraveText: boolean | null = null;
    textVertical: boolean | null = null;
    textDepth: number | null = null;
    textHeight: number | null = null;
    textRepeat: number | null = null;
    textX: number | null = null;
    textY: number | null = null;
    textZ: number | null = null;

    engraveUID: boolean | null = null;
    UIDVertical: boolean | null = null;
    UIDDepth: number | null = null;
    UIDHeight: number | null = null;
    UIDRepeat: number | null = null;
    UIDX:number | null = null;
    UIDY:number | null = null;
    UIDZ:number | null = null;

    engraveCE: boolean | null = null;
    CEVertical: boolean | null = null;
    CEDepth: number | null = null;
    CEHeight: number | null = null;
    CEX:number | null = null;
    CEY:number | null = null;
    CEZ:number | null = null;

    constructor(init?: Partial<EngravingResponseDto>){
        if (init) assignMatchingFields(this, init);
    }

    asJSON() {
        return JSON.stringify(this);
    }

    toEngravingModel(overwriteModel?: Partial<Engraving>) {
        let engravingModel = new Engraving(this);
        if (overwriteModel) assignMatchingFields(engravingModel, overwriteModel);
        if ((this.textX != null && this.textX != undefined) ||
            (this.textY != null && this.textY != undefined) ||
            (this.textZ != null && this.textZ != undefined))
            engravingModel.textPosition = new THREE.Vector3(this.textX ?? undefined, this.textY ?? undefined, this.textZ ?? undefined);
        if ((this.UIDX != null && this.UIDX != undefined) ||
            (this.UIDY != null && this.UIDY != undefined) ||
            (this.UIDZ != null && this.UIDZ != undefined))
            engravingModel.UIDPosition = new THREE.Vector3(this.UIDX ?? undefined, this.UIDY ?? undefined, this.UIDZ ?? undefined);
        if ((this.CEX != null && this.CEX != undefined) ||
            (this.CEY != null && this.CEY != undefined) ||
            (this.CEZ != null && this.CEZ != undefined))
            engravingModel.CEPosition = new THREE.Vector3(this.CEX ?? undefined, this.CEY ?? undefined, this.CEZ ?? undefined);
        
        engravingModel.updateText();
        engravingModel.updateUID();
        engravingModel.updateCE();

        return engravingModel;
    }

    toEngravingRequestDto() {
        return new EngravingRequestDto(this);
    }
}
